import { useMemo, useState } from 'react';

import { apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { formatDateTime } from '@app/lib/format';
import { StatusBadge } from '@app/components/StatusBadge';

interface TxRow {
  id: string;
  customer_email: string;
  customer_company: string;
  invoice_number: string;
  transaction_type: string;
  status: string;
  amount: string;
  signed_amount: string;
  currency: string;
  payment_gateway: string;
  reference_number: string;
  description: string;
  created_at: string;
}

const repo = new AdminListRepository<TxRow>(apiClient, 'transactions');

const columns: DataTableColumn<TxRow>[] = [
  { id: 'created_at', header: 'When', accessor: (r) => formatDateTime(r.created_at) },
  {
    id: 'customer_email', header: 'Customer',
    accessor: (r) => (
      <span>
        {r.customer_company}
        <span className="block text-xs text-slate-500">{r.customer_email}</span>
      </span>
    ),
  },
  { id: 'transaction_type', header: 'Type', accessor: 'transaction_type' },
  {
    // Balance effect: the ledger stores positive amounts, the backend signs them by type
    id: 'amount', header: 'Amount',
    accessor: (r) => {
      const n = Number(r.signed_amount);
      return (
        <span className={n < 0 ? 'text-red-700' : 'text-green-700'}>
          {n < 0 ? '−' : '+'}${Math.abs(n).toFixed(2)} {r.currency}
        </span>
      );
    },
  },
  { id: 'description', header: 'Description', accessor: 'description' },
  { id: 'status', header: 'Status', accessor: (r) => <StatusBadge value={r.status} /> },
  { id: 'payment_gateway', header: 'Gateway', accessor: 'payment_gateway' },
  { id: 'reference_number', header: 'Ref', accessor: 'reference_number' },
];

export function TransactionsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const filters = useMemo(() => {
    const f: Record<string, string> = {};
    if (search) f.search = search;
    if (statusFilter) f.status = statusFilter;
    if (typeFilter) f.type = typeFilter;
    return f;
  }, [search, statusFilter, typeFilter]);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-semibold">Transactions</h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <input
            type="search"
            placeholder="Search customer, reference, description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded border px-2 py-1"
          />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded border px-2 py-1"
          >
            <option value="">All types</option>
            <option value="payment">Payment</option>
            <option value="refund">Refund (given back)</option>
            <option value="credit">Credit</option>
            <option value="debit">Debit</option>
            <option value="adjustment">Adjustment</option>
            <option value="chargeback">Chargeback</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border px-2 py-1"
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
      </div>
      <DataTable<TxRow>
        columns={columns}
        fetcher={(p) => repo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={25}
        rowKey={(r) => r.id}
        emptyState={<span data-testid="datatable-empty">No transactions yet.</span>}
      />
    </section>
  );
}
